/* eslint-disable no-warning-comments */
import classNames from 'classnames'
import {debounce} from 'lodash'
import * as React from 'react'

import {handleHubspotResponse} from '@/common/utils'
import Content from '@/components/Content'
import {isValidEmail} from '@/components/Form/helpers'
import Heading from '@/components/Heading'
import RichText from '@/components/RichText/Richtext'

const RichTextForm = ({
  headingTag,
  heading,
  content,
  portalId,
  formId,
  inputs,
  submitLabel,
  loadingMessage,
  dataCollect,
  title,
}) => {
  const HeadingTag = headingTag || 'p'
  inputs = inputs || []

  const [isLoading, setIsLoading] = React.useState(false)
  const [success, setSuccess] = React.useState()
  const [error, setError] = React.useState()
  const [siretNumber, setSiretNumber] = React.useState('')
  const [searchCompanyResult, setSearchCompanyResult] = React.useState([])
  const companyInput = React.useRef()
  const siretInput = React.useRef()

  const [color, setColor] = React.useState('#bea9c2')

  const handleChange = (event) => {
    setColor(event.target.value ? '#280c2d' : '#bea9c2')
  }

  const searchCompany = async (input) => {
    const company = input.target.value
    const result = await fetch(`/crm_siret?term=` + company)
    const resultData = await result.json()
    setSearchCompanyResult(resultData.results)
  }

  const debounceSearch = debounce(searchCompany, 300)

  const selectCompany = (result) => {
    companyInput.current.value = result
    setSearchCompanyResult([])
  }

  const setSiret = (siret) => {
    siretInput.current.value = siret
    setSiretNumber(siret)
  }

  const className =
    'not-prose overflow-visible flex flex-col gap-6 rounded-2xl bg-yellow-400 px-6 py-10 md:px-19'

  async function handleSubmit(e) {
    e.preventDefault()
    try {
      setIsLoading(true)
      setSuccess()
      setError()

      const form = e.target
      const formData = new FormData(form)
      const pageUrl = window.location.href

      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)

      const email = formData.get('email')

      if (!(await isValidEmail(email))) {
        setError('Vous devez renseigner une adresse e-mail professionnelle.')
        const input = form.querySelector("input[name='email']")
        if (input) input.focus()
        return
      }

      const fields = []
      for (const [name, value] of formData) {
        fields.push({name, value})
      }

      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
        {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: [
              ...fields,
              {
                name: 'utm_campaign',
                value: urlParams.get('utm_campaign') || formData.get('utm_campaign') || '',
              },
              {
                name: 'utm_source',
                value: urlParams.get('utm_source') || formData.get('utm_source') || '',
              },
              {
                name: 'utm_term',
                value: urlParams.get('utm_term') || formData.get('utm_term') || '',
              },
            ],
            context: {
              pageUri: pageUrl,
              pageName: title,
            },
          }),
        }
      )

      if (response.ok) {
        await handleHubspotResponse(response, (message) => setSuccess(message))
      } else {
        const {errors = []} = await response.json()
        throw Error(errors.map(({message}) => `${message}.`).join(' - '))
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className={className}>
        <p
          className='body-s text-center text-purple-900'
          dangerouslySetInnerHTML={{__html: success}}
        />
      </div>
    )
  }

  return (
    <form className={className} onSubmit={handleSubmit}>
      <div className='flex flex-col gap-4'>
        <HeadingTag>
          <Heading heading={heading} className='heading-xs-millik text-center' />
        </HeadingTag>
        <Content content={content} className='text-center' size='m' />
      </div>
      <fieldset className='relative grid gap-2 md:grid-cols-2'>
        {inputs.map(
          ({
            _uid,
            component,
            hidden,
            size,
            required,
            type,
            name,
            label,
            isCompanySearch,
            isSiretNumber,
            options,
          }) => {
            label = label + (required ? '*' : '')

            return (
              <React.Fragment key={_uid}>
                {component === 'input' ? (
                  <label htmlFor={_uid} className='sr-only'>
                    {label}
                  </label>
                ) : (
                  <label
                    className='body-l-bold col-span-full mt-4  w-full text-center'
                    htmlFor={_uid}
                  >
                    {label}
                  </label>
                )}

                {isCompanySearch ? (
                  <div className='!relative col-span-full flex w-full flex-col'>
                    <div className='flex w-full'>
                      <input
                        id={_uid}
                        hidden={hidden}
                        required={required}
                        type={type || 'text'}
                        name={name}
                        placeholder={label}
                        className='body-m-medium w-full rounded-l-lg border border-transparent bg-white px-2 py-3 text-purple-900 placeholder:text-purple-300 focus:border-violet-600 focus:outline-0 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]'
                        onKeyUp={debounceSearch}
                        ref={companyInput}
                        autoComplete='off'
                      />
                      {siretNumber !== '' && (
                        <span className='body-s-medium w-full whitespace-nowrap rounded-r-lg border border-transparent bg-white px-2 py-3 text-violet-400 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]'>
                          N° {siretNumber}
                        </span>
                      )}
                    </div>
                    <div id='search-company-result' className=''>
                      <ul className='absolute z-20 max-h-60 w-full overflow-auto rounded-lg bg-red-800 md:shadow-[0px_12px_12px_rgba(0,0,0,0.04)]'>
                        {searchCompanyResult?.map((result) => {
                          return (
                            <li
                              key={result.siret}
                              className='body-m-medium w-full cursor-pointer border border-transparent bg-white p-2 text-purple-900 last:rounded-b-lg'
                              onClick={() => {
                                selectCompany(result.name)
                                setSiret(result.siret)
                              }}
                            >
                              <div className='flex w-full justify-between rounded-lg px-2 py-3 hover:bg-yellow-400'>
                                <span className='max-w-1/2 block flex-1'>{result.name}</span>
                                <span className='body-s-medium flex-1 whitespace-nowrap text-violet-400'>
                                  N° Siret {result.siret}
                                </span>
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  </div>
                ) : isSiretNumber ? (
                  <input
                    id={_uid}
                    hidden={hidden}
                    required={required}
                    type={type || 'text'}
                    name={name}
                    placeholder={label}
                    className={classNames(
                      'body-m-medium w-full rounded-lg px-2 py-3 ',
                      size === 'small' ? 'col-span-1' : 'col-span-2'
                    )}
                    ref={siretInput}
                    autoComplete='off'
                  />
                ) : component === 'input' ? (
                  <input
                    id={_uid}
                    hidden={hidden}
                    required={required}
                    type={type || 'text'}
                    name={name}
                    placeholder={label}
                    className={classNames(
                      'body-m-medium w-full rounded-lg px-2 py-3 text-purple-900 placeholder:text-purple-300 focus:border-violet-600 focus:outline-0',
                      size === 'small' ? 'col-span-1' : 'col-span-2'
                    )}
                  />
                ) : options.length === 1 ? (
                  <>
                    <input id={_uid} hidden type='text' name={name} value={options[0].value} />
                    <span
                      className={classNames(
                        'body-m-medium w-full bg-transparent px-2 py-3 text-center text-purple-900',
                        size === 'small' ? 'col-span-1' : 'col-span-2'
                      )}
                    >
                      {options[0].date}
                    </span>
                  </>
                ) : (
                  options.length > 1 && (
                    <select
                      onChange={handleChange}
                      style={{color}}
                      id={_uid}
                      name={name}
                      className={classNames(
                        'body-m-medium w-full rounded-lg border border-transparent bg-white px-2 py-3 focus:border-violet-600 focus:outline-0 md:shadow-[0px_12px_12px_rgba(0,0,0,0.02)]',
                        size === 'small' ? 'col-span-1' : 'col-span-2'
                      )}
                    >
                      {options.map(({_uid, value, date}, index) => {
                        return (
                          <option
                            id={_uid}
                            selected={index === 0 ? 'selected' : ''}
                            disabled={index === 0 ? 'disabled' : ''}
                            hidden={index === 0 ? 'hidden' : ''}
                            value={index === 0 ? '' : value}
                            className='body-m-medium text-purple-900'
                          >
                            {date ? date : value}
                          </option>
                        )
                      })}
                    </select>
                  )
                )}
              </React.Fragment>
            )
          }
        )}

        <Doodle />
      </fieldset>

      {!!error && <p className='body-s w-full text-center text-red-800'>{error}</p>}

      <button
        className={classNames(
          'misc-button-s block self-center rounded-full border border-purple-900 bg-purple-900 px-6 py-2 text-white',
          {
            'transition hover:bg-transparent hover:text-purple-900': !isLoading,
          }
        )}
        disabled={isLoading}
      >
        {isLoading ? loadingMessage : submitLabel}
      </button>
      {dataCollect && (
        <div className='text-center'>
          <RichText richtext={dataCollect} />
        </div>
      )}
    </form>
  )
}

const Doodle = () => (
  <svg
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 153 156'
    className='absolute bottom-0 left-0 w-15 -translate-x-1/2 translate-y-1/2 md:w-40'
  >
    <g clipPath='url(#rich-text-form-doodle)'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M43.83 96.32a.96.96 0 0 1 .33-1.32l25.23-15.22a.96.96 0 1 1 .99 1.64L45.15 96.64a.96.96 0 0 1-1.32-.32ZM18.36 49.09a.96.96 0 0 1 .57-1.23c1.08-.4 2.9-.58 4.99-.66a108 108 0 0 1 7.38.04c5.35.18 11.16.6 14.89.95a.96.96 0 0 1-.18 1.9c-3.7-.33-9.47-.76-14.77-.94-2.65-.09-5.17-.11-7.25-.03-2.12.08-3.65.27-4.4.54a.96.96 0 0 1-1.23-.57Zm37.13-21.94a.96.96 0 1 0 1.27-1.44l-16.4-14.55a.96.96 0 0 0-1.27 1.43l16.4 14.56Zm66.86 110.96a.96.96 0 0 1-.5-1.26l.03-.11.03-.37c.02-.31.02-.72-.01-1.2-.05-.98-.19-2.21-.36-3.54-.36-2.65-.88-5.62-1.25-7.54a.96.96 0 0 1 1.88-.36c.38 1.94.9 4.95 1.27 7.65a49 49 0 0 1 .37 3.68c.03.52.04 1 .02 1.41-.02.38-.07.8-.22 1.15a.96.96 0 0 1-1.26.5ZM83.93 127.4a.96.96 0 0 0 1.83.56l6.4-21.03a.96.96 0 0 0-1.83-.55l-6.4 21.02Z'
        className='fill-violet-600'
      />
    </g>
    <defs>
      <clipPath id='rich-text-form-doodle'>
        <path fill='#fff' transform='rotate(-48.42 48.24 35.06)' d='M0 0h63.02v161.58H0z' />
      </clipPath>
    </defs>
  </svg>
)

export default RichTextForm
